import React from "react";
import styled, { keyframes } from "styled-components";
import * as Router from "next/router";
import Head from "next/head";
import Image from "next/image";

import * as u from "../../utils";
import * as gtag from "../../utils/gtag";
import * as H from "../../utils/hooks";
import * as T from "../../components/Typography";
import * as I from "../../components/Icons";
import BackButton from "../../components/BackButton";
import ImageInitials from "../../components/ImageInitials";
import ConnectButton from "../../components/ConnectButton";
import Error404 from "../../components/Error404";
import LoadingSpinner from "../../components/LoadingSpinner";

export default function MemberProfile() {
  const router = Router.useRouter();
  const { data: memberInfo, isLoading } = H.useFireDoc(
    "users",
    router.query.member
  );
  const interests = memberInfo?.interests?.join(" • ");
  const seeking = memberInfo?.seeking?.join(" • ");

  if (isLoading) return <LoadingSpinner />;

  if (!memberInfo || memberInfo.disabled) return <Error404 />;

  return (
    <S.Container>
      <Head>
        <title>
          {memberInfo?.name.trim() + " | AllBright Member | AllBright"}
        </title>
      </Head>
      <BackButton />
      <S.MemberContainer>
        {getInfo()}
        {getBio()}
        <S.ABLogoWrap>
          <Image
            src="/images/AllBrightA.png"
            height="250"
            width="250"
            objectFit="cover"
          />
        </S.ABLogoWrap>
      </S.MemberContainer>
    </S.Container>
  );

  function getInfo() {
    const imgProps = { ...memberInfo, size: "xxxl" };
    const jobTitle = memberInfo?.jobTitle || "";
    const jobCompany = memberInfo?.jobCompany || "";
    const occupation = [jobTitle, jobCompany].filter((a) => a).join(" • ");
    const city = memberInfo?.city;
    const country = memberInfo?.country;
    const location = [city, country].filter((a) => a).join(", ");

    return (
      <S.Info>
        <SI.Container>
          <T.H3>{memberInfo?.name}</T.H3>
          <SI.Initials>
            <ImageInitials {...imgProps} />
          </SI.Initials>
          <T.CL weight="500">{occupation}</T.CL>
          {memberInfo.isAmbassador && <T.CL>AllBright Ambassador</T.CL>}
          <S.Row>
            <I.Location />
            <T.CL weight="500">{location}</T.CL>
          </S.Row>
          <ConnectButton
            id={memberInfo?.id}
            name={memberInfo.name}
            canRemoveConnection
          />
        </SI.Container>
      </S.Info>
    );
  }

  function getBio() {
    const bioFields = [
      { title: "About Me", info: memberInfo?.bio },
      { title: "Interests", info: interests },
      { title: "Seeking", info: seeking },
      { title: "Job Level", info: memberInfo?.jobLevel },
      { title: "Industry", info: memberInfo?.jobIndustry },
      {
        title: "Instagram",
        info: memberInfo?.instagram,
        onClick: () => onClickLink("Instagram"),
        isLink: true,
      },
      {
        title: "Website",
        info: memberInfo?.website?.toLowerCase(),
        onClick: () => onClickLink("website"),
        isLink: true,
      },
      {
        title: "LinkedIn",
        info: memberInfo?.linkedin?.toLowerCase(),
        onClick: () => onClickLink("LinkedIn"),
        isLink: true,
      },
    ];

    return (
      <S.Bio>
        {bioFields
          .filter((field) => field.info)
          .map((field, index) => (
            <Bio {...field} index={index} key={field.title} />
          ))}
      </S.Bio>
    );

    function onClickLink(platform) {
      gtag.event({
        name: gtag.GA_EVENT.SOCIAL_ENGAGEMENT,
        category: "Engagement",
        label: "Member Page",
        value: platform,
      });
    }
  }
}

function Bio(props) {
  if (!props.info) return null;

  return (
    <S.BioRow>
      <S.RowColour isEven={props.index % 2 == 0} />
      <S.RowText>
        <T.H6>{props.title}</T.H6>
        {props.isLink ? (
          <div>
            <T.A href={getMaybeLink()}>{props.info}</T.A>
          </div>
        ) : (
          <T.CL allowWhiteSpace>{props.info}</T.CL>
        )}
      </S.RowText>
    </S.BioRow>
  );

  function getMaybeLink() {
    if (props?.info?.startsWith("http")) return props?.info;
    if (props?.field === "Instagram")
      return [
        "https://www.instagram.com/",
        props?.info?.split("@")[1] || props?.info,
      ].join("");

    return `http://${props?.info}`;
  }
}

const S = {};

S.Container = styled.div`
  bottom: 20px;
  left: 0;
  right: 0;
  top: 60px;
  margin: 0 auto;
  padding: 15px 16px 20px;

  @media ${u.device.mobile} {
    max-width: 1000px;
    padding: 20px 40px;
  }
`;

S.MemberContainer = styled.div`
  background: ${u.colours.lightShell};
  position: relative;
  margin-top: 20px;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 0 15px ${u.colours.charcoalFaded};

  @media ${u.device.mobile} {
    max-height: 500px;
    display: flex;
  }
`;

S.ABLogoWrap = styled.div`
  position: absolute;
  opacity: 0.6;
  top: 0;
  right: 0;
  left: 0;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  @media ${u.device.mobile} {
    right: 20px;
    top: 10px;
    left: auto;
  }
`;

S.Fade = keyframes`
  0% { opacity: 0 }
  100% { opacity: 1 }
`;

S.Info = styled.div`
  position: relative;
  align-items: center;
  display: flex;
  flex: 2;
  flex-direction: column;
  padding: 36px 20px;
  z-index: 2;
`;

S.Bio = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 3;
  padding: 0;
  overflow-y: auto;

  @media ${u.device.mobile} {
    padding: 60px 0 0 0;
  }
`;

S.BioRow = styled.div`
  position: relative;
  padding: 10px 40px;
`;

S.RowColour = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  background: ${(p) => (p.isEven ? u.colours.lightShell : u.colours.shell)};
  left: 0;
  top: 0;
`;

S.RowText = styled.div`
  position: relative;
  z-index: 2;
`;

S.Loader = styled.article`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

S.Row = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const SI = {};

SI.Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  height: 100%;
  justify-content: space-evenly;
  gap: 15px;
`;

SI.Initials = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 0;
  animation: ${S.Fade} 1s ease-out forwards;
`;
